import {
  PlatformIdentityV1Schema,
  LogoutResponseV1Schema,
  SuiteContextV1Schema,
  type PlatformIdentityV1,
  type SuiteContextV1,
} from "@fulcrum5/suite-contracts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Type } from "typebox";
import { Value } from "typebox/value";
import { PlatformApi } from "../api/platformApi.ts";

const CsrfTokenSchema = Type.String({
  minLength: 45,
  maxLength: 128,
  pattern: "^[1-9][0-9]*\\.[A-Za-z0-9_-]{43}$",
});

const PlatformSessionResponseV1Schema = Type.Union([
  Type.Object({
    version: Type.Literal(1),
    authenticated: Type.Literal(false),
  }, { additionalProperties: false }),
  Type.Object({
    version: Type.Literal(1),
    authenticated: Type.Literal(true),
    identity: PlatformIdentityV1Schema,
    csrfToken: CsrfTokenSchema,
    suiteContext: Type.Union([SuiteContextV1Schema, Type.Null()]),
  }, { additionalProperties: false }),
]);

type PlatformSessionResponseV1 =
  | Readonly<{
    version: 1;
    authenticated: false;
  }>
  | Readonly<{
    version: 1;
    authenticated: true;
    identity: PlatformIdentityV1;
    csrfToken: string;
    suiteContext: SuiteContextV1 | null;
  }>;

export type PlatformSessionPhase =
  | "loading"
  | "unauthenticated"
  | "access-pending"
  | "ready"
  | "error";

export interface PlatformSessionState {
  readonly phase: PlatformSessionPhase;
  readonly identity: PlatformIdentityV1 | null;
  readonly suiteContext: SuiteContextV1 | null;
  readonly csrfToken: string | null;
  readonly message: string | null;
}

export interface PlatformSessionContextValue extends PlatformSessionState {
  readonly api: PlatformApi;
  refresh(): Promise<void>;
  logout(path: string): Promise<void>;
}

export interface PlatformSessionProviderProps {
  readonly children: ReactNode;
  readonly fetchImpl?: typeof fetch;
  readonly onUnauthenticated?: () => void;
  /** One opaque id per mounted provider; used only for app-open idempotency. */
  readonly pageLoadEventIdFactory?: () => string | null;
}

const INITIAL_STATE: PlatformSessionState = {
  phase: "loading",
  identity: null,
  suiteContext: null,
  csrfToken: null,
  message: null,
};

const PlatformSessionContext = createContext<PlatformSessionContextValue | null>(null);
const defaultBrowserFetch: typeof fetch = (input, init) => globalThis.fetch(input, init);

function resolvePhase(identity: PlatformIdentityV1): "access-pending" | "ready" {
  return identity.activeOrganizationId === null && !identity.isF5Admin
    ? "access-pending"
    : "ready";
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * Default unauthenticated hook is intentionally a no-op.
 * Auto-navigating to /auth/login raced OAuth state (Strict Mode double-mount)
 * and left users stuck on "Signing in" after logout. App renders SignInPage;
 * the user starts WorkOS with an explicit Sign in click.
 */
function defaultUnauthenticatedRedirect(): void {
  // no-op
}

function defaultPageLoadEventId(): string | null {
  const crypto = globalThis.crypto;
  return typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : null;
}

/**
 * Owns opaque browser-session state. Components only receive the validated
 * identity and a request client; neither exposes the HttpOnly cookie.
 */
export function PlatformSessionProvider({
  children,
  fetchImpl = defaultBrowserFetch,
  onUnauthenticated = defaultUnauthenticatedRedirect,
  pageLoadEventIdFactory = defaultPageLoadEventId,
}: PlatformSessionProviderProps): JSX.Element {
  const [state, setState] = useState<PlatformSessionState>(INITIAL_STATE);
  const [pageLoadEventId] = useState(pageLoadEventIdFactory);
  const hasNotifiedUnauthenticated = useRef(false);

  const notifyUnauthenticated = useCallback(() => {
    if (hasNotifiedUnauthenticated.current) return;
    hasNotifiedUnauthenticated.current = true;
    setState({
      phase: "unauthenticated",
      identity: null,
      suiteContext: null,
      csrfToken: null,
      message: null,
    });
    onUnauthenticated();
  }, [onUnauthenticated]);

  const refresh = useCallback(async () => {
    setState((current) => current.phase === "loading" ? current : INITIAL_STATE);
    let response: Response;
    try {
      response = await fetchImpl("/api/session", {
        credentials: "same-origin",
        headers: {
          accept: "application/json",
          ...(pageLoadEventId ? { "x-platform-page-load-id": pageLoadEventId } : {}),
        },
      });
    } catch {
      setState({
        phase: "error",
        identity: null,
        suiteContext: null,
        csrfToken: null,
        message: "Platform session could not be checked",
      });
      return;
    }

    if (response.status === 401) {
      setState({ phase: "unauthenticated", identity: null, suiteContext: null, csrfToken: null, message: null });
      notifyUnauthenticated();
      return;
    }

    const payload = await readJson(response);
    if (!response.ok || !Value.Check(PlatformSessionResponseV1Schema, payload)) {
      setState({
        phase: "error",
        identity: null,
        suiteContext: null,
        csrfToken: null,
        message: "Platform session returned an invalid response",
      });
      return;
    }

    // TypeBox's static tuple inference intentionally stays conservative for the
    // context union. The runtime contract above has already validated this
    // browser boundary against the exported SuiteContext type.
    const sessionPayload = payload as PlatformSessionResponseV1;

    if (!sessionPayload.authenticated) {
      setState({ phase: "unauthenticated", identity: null, suiteContext: null, csrfToken: null, message: null });
      notifyUnauthenticated();
      return;
    }

    setState({
      phase: resolvePhase(sessionPayload.identity),
      identity: sessionPayload.identity,
      suiteContext: sessionPayload.suiteContext,
      csrfToken: sessionPayload.csrfToken,
      message: null,
    });
  }, [fetchImpl, notifyUnauthenticated, pageLoadEventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const api = useMemo(() => new PlatformApi({
    csrfToken: () => state.csrfToken,
    fetchImpl,
    onUnauthenticated: notifyUnauthenticated,
  }), [fetchImpl, notifyUnauthenticated, state.csrfToken]);

  const logout = useCallback(async (path: string) => {
    await api.request({
      path,
      method: "POST",
      responseSchema: LogoutResponseV1Schema,
    });
    hasNotifiedUnauthenticated.current = false;
    setState({ phase: "unauthenticated", identity: null, suiteContext: null, csrfToken: null, message: null });
  }, [api]);

  const value = useMemo<PlatformSessionContextValue>(() => ({
    ...state,
    api,
    refresh,
    logout,
  }), [api, logout, refresh, state]);

  return (
    <PlatformSessionContext.Provider value={value}>
      {children}
    </PlatformSessionContext.Provider>
  );
}

export function usePlatformSession(): PlatformSessionContextValue {
  const session = useContext(PlatformSessionContext);
  if (!session) throw new Error("usePlatformSession must be used inside PlatformSessionProvider");
  return session;
}
